import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineOfficeBuilding, HiOutlineChevronDown, HiOutlineCheck } from 'react-icons/hi';
import { useClinic } from '../../context/ClinicContext';

const ClinicSwitcher = () => {
  const { clinics, currentClinic, currentClinicId, setCurrentClinicId, loading } = useClinic();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading && clinics.length === 0) {
    return null;
  }

  if (clinics.length === 0) {
    return null;
  }

  // If only one clinic, render a static label without dropdown.
  if (clinics.length === 1) {
    return (
      <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700 truncate max-w-[140px] lg:max-w-[200px]">
          {clinics[0].name}
        </span>
      </div>
    );
  }

  const label = currentClinic?.name || 'Select clinic';

  const handleSelect = (id) => {
    if (id !== currentClinicId) {
      setCurrentClinicId(id);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors"
      >
        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-500" />
        <span className="hidden sm:inline text-sm font-medium text-gray-800 truncate max-w-[120px] md:max-w-[160px] lg:max-w-[220px]">
          {label}
        </span>
        <HiOutlineChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 animate-slide-down">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Switch clinic
          </div>
          <div className="max-h-72 overflow-y-auto">
            {clinics.map((clinic) => {
              const isActive = clinic.id === currentClinicId;
              return (
                <button
                  key={clinic.id}
                  type="button"
                  onClick={() => handleSelect(clinic.id)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${isActive ? 'bg-primary-50' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className={`truncate ${isActive ? 'text-primary-700 font-semibold' : 'text-gray-800'}`}>
                      {clinic.name}
                    </p>
                    {clinic.role_name && (
                      <p className="text-xs text-gray-500 capitalize truncate">{clinic.role_name}</p>
                    )}
                  </div>
                  {isActive && <HiOutlineCheck className="w-4 h-4 text-primary-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicSwitcher;
